import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import ProductModal from '../../../components/ProductModal';

import { analyticsService } from '../../../services/analyticsService';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { getHotspotStyle, getWidthHeightFromAspectRatio } from '../../../utils/coordinateSystem';
import { getVideoEmbedUrl } from 'utils/getVideoEmbedUrl';

// Memoized hotspot renderer to prevent unnecessary re-renders
const HotspotMarker = React.memo(({ hotspot, side, pageContainerRefs, hoveredHotspot, onHover, onLeave, onClick }) => {
  const pageContainer = pageContainerRefs?.current?.[side];
  const style = pageContainer 
    ? getHotspotStyle(hotspot, pageContainer)
    : { left: '0px', top: '0px', width: '200px', height: '200px' };
  const isBuyDisabled = hotspot?.type === 'buy' && !hotspot?.sku;

  return (
    <div
      className="absolute group z-50"
      style={style}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      {/* Image Hotspot */}
      {hotspot?.type === 'image' && hotspot?.imageUrl && (
        <div className="w-full h-full rounded-md overflow-hidden shadow-lg cursor-pointer transition-transform duration-250 hover:scale-105">
          <Image
            src={hotspot?.imageUrl}
            alt={hotspot?.title || 'Hotspot image'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Video Hotspot */}
      {hotspot?.type === 'video' && hotspot?.videoUrl && (
        <div className="relative w-full h-full rounded-md overflow-hidden shadow-lg">
          <iframe
            src={getVideoEmbedUrl(hotspot?.videoUrl)}
            title={hotspot?.title || 'Hotspot video'}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      )}

      {/* Buy Button Hotspot */}
      {hotspot?.type === 'buy' && (
        <button
          className={`w-full h-full rounded-md shadow-lg transition-all duration-250 flex items-center justify-center gap-1 px-1 py-1 overflow-hidden ${
            isBuyDisabled
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white cursor-pointer hover:scale-105'
          }`}
          aria-disabled={isBuyDisabled}
          title={isBuyDisabled ? 'SKU required to open product' : undefined}
        >
          <Icon name="ShoppingCart" size={16} className="flex-shrink-0" />
          <span className="font-semibold truncate text-[clamp(9px,1.5cqw,14px)]">
            {hotspot?.buttonText || (isBuyDisabled ? 'SKU Required' : 'Add to Cart')}
          </span>
        </button>
      )}

      {/* Link Button Hotspot */}
      {hotspot?.type === 'link' && (
        <button
          className="w-full h-full text-white rounded-md shadow-lg transition-all duration-250 hover:scale-105 hover:brightness-90 flex items-center justify-center gap-1 px-1 py-1 overflow-hidden cursor-pointer"
          style={{ backgroundColor: hotspot?.buttonColor || '#2563EB' }}
        >
          <Icon name="ExternalLink" size={16} className="flex-shrink-0" />
          <span className="font-semibold truncate text-[clamp(9px,1.5cqw,14px)]">
            {hotspot?.buttonText || 'Learn More'}
          </span>
        </button>
      )}

      {/* Info Hotspot */}
      {hotspot?.type === 'info' && (
        <button
          className="w-full h-full text-white rounded-md shadow-lg transition-all duration-250 hover:scale-105 hover:brightness-90 flex items-center justify-center gap-1 px-1 py-1 overflow-hidden cursor-pointer"
          style={{ backgroundColor: hotspot?.buttonColor || '#7C3AED' }}
        >
          <Icon name="Info" size={16} className="flex-shrink-0" />
          <span className="font-semibold truncate text-[clamp(9px,1.5cqw,14px)]">
            {hotspot?.title || hotspot?.buttonText || 'Info'}
          </span>
        </button>
      )}

      {/* Internal Link Hotspot - Invisible clickable area */}
      {hotspot?.type === 'internal_link' && (
        <div className="w-full h-full cursor-pointer">
          {/* Completely transparent - no visual indicator */}
        </div>
      )}
      
      {/* Tooltip on Hover */}
      {hoveredHotspot === hotspot?.id && (hotspot?.type === 'info' ? hotspot?.description : hotspot?.title) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-popover text-popover-foreground text-xs font-medium rounded-md shadow-lg whitespace-nowrap z-[60] pointer-events-none">
          {hotspot?.type === 'info' ? hotspot?.description : hotspot?.title}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-popover rotate-45" />
        </div>
      )}
    </div>
  );
});

HotspotMarker.displayName = 'HotspotMarker';

const FlipbookViewer = ({ catalog, pages, onPageChange, currentPage, embedMode = false, showZoom = true, showFullscreen = true, showNav = true, viewerMode = 'flip' }) => {
  const { t } = useTranslation();
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [imageLoading, setImageLoading] = useState({ left: false, right: false });
  const [imageError, setImageError] = useState({ left: false, right: false });
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [selectedProductSku, setSelectedProductSku] = useState(null);
  const [selectedProductStoreUrl, setSelectedProductStoreUrl] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  // Spread entrante para animación slider (pre-renderizado simultáneamente)
  const [incomingSpreadData, setIncomingSpreadData] = useState(null);
  const viewerRef = useRef(null);
  const pageContainerRefs = useRef({ left: null, right: null });
  const preloadedImages = useRef(new Map());
  const flipTimeoutRef = useRef(null);
  // Force recalculation of page dimensions once the viewer mounts and has real dimensions
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (viewerRef.current) forceUpdate(n => n + 1);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending flip timeout
      if (flipTimeoutRef?.current) {
        clearTimeout(flipTimeoutRef?.current);
        flipTimeoutRef.current = null;
      }
      // Nullificar src de cada Image para liberar memoria antes de limpiar el Map
      preloadedImages.current.forEach(img => { img.src = ''; });
      preloadedImages.current.clear();
      // Null out DOM refs to prevent detached node retention
      pageContainerRefs.current.left = null;
      pageContainerRefs.current.right = null;
    };
  }, []);

  const companyId = useMemo(() => catalog?.companyId, [catalog?.companyId]);

  // CRITICAL: Sort pages by page_number to ensure correct ordering
  const sortedPages = useMemo(() => {
    if (!catalog?.catalogPages) return [];
    return [...catalog?.catalogPages]?.sort((a, b) => (a?.pageNumber || 0) - (b?.pageNumber || 0));
  }, [catalog?.catalogPages]);

  const totalPages = sortedPages?.length;
  
  // MAGAZINE LAYOUT LOGIC (CRITICAL)
  const newCurrentPage =  currentPage === 0 ? 1 : currentPage;
  const isFrontCover = newCurrentPage === 1;
  const isBackCover = newCurrentPage === totalPages;
  const isSpread = !isFrontCover && !isBackCover;
  const getWidthHeightStyle = getWidthHeightFromAspectRatio({ viewerRef, isFrontCover, isBackCover });

  // Calcula el spread (left/right) para cualquier número de página dado
  const getSpreadForPage = useCallback((pageNum) => {
    if (!pageNum || !sortedPages?.length) return { left: null, right: null };
    const p = pageNum === 0 ? 1 : pageNum;
    if (p === 1) return { left: null, right: sortedPages?.find(pg => pg?.pageNumber === 1) };
    if (p === totalPages) return { left: sortedPages?.find(pg => pg?.pageNumber === totalPages), right: null };
    const leftNum = p % 2 === 0 ? p : p - 1;
    const rightNum = leftNum + 1;
    return {
      left: sortedPages?.find(pg => pg?.pageNumber === leftNum),
      right: rightNum <= totalPages ? sortedPages?.find(pg => pg?.pageNumber === rightNum) : null,
    };
  }, [sortedPages, totalPages]);

  // Get left and right pages for current view - memoized
  const getSpreadPages = useCallback(() => {
    if (isFrontCover) {
      return { left: null, right: sortedPages?.find(p => p?.pageNumber === 1) };
    } else if (isBackCover) {
      return { left: sortedPages?.find(p => p?.pageNumber === totalPages), right: null };
    } else {
      const leftPageNum = newCurrentPage % 2 === 0 ? newCurrentPage : newCurrentPage - 1;
      const rightPageNum = leftPageNum + 1;
      return {
        left: sortedPages?.find(p => p?.pageNumber === leftPageNum),
        right: rightPageNum <= totalPages ? sortedPages?.find(p => p?.pageNumber === rightPageNum) : null
      };
    }
  }, [newCurrentPage, sortedPages, isFrontCover, isBackCover, totalPages]);

  const { left: leftPage, right: rightPage } = useMemo(() => getSpreadPages(), [getSpreadPages]);

  // Preload adjacent pages for smoother transitions
  useEffect(() => {
    const preloadImage = (url) => {
      if (!url || preloadedImages.current.has(url)) return;
      const img = new window.Image();
      img.src = url;
      preloadedImages.current.set(url, img);
    };

    // Preload next pages
    if (newCurrentPage < totalPages) {
      const nextPageNum = isFrontCover ? 2 : Math.min(totalPages, newCurrentPage + 2);
      const nextPages = sortedPages?.filter(p => 
        p?.pageNumber === nextPageNum || p?.pageNumber === nextPageNum + 1
      );
      nextPages?.forEach(page => preloadImage(page?.imageUrl));
    }

    // Preload previous pages
    if (newCurrentPage  > 1) {
      const prevPageNum = newCurrentPage === 2 ? 1 : Math.max(2, newCurrentPage - 2);
      const prevPages = sortedPages?.filter(p => 
        p?.pageNumber === prevPageNum || p?.pageNumber === prevPageNum - 1
      );
      prevPages?.forEach(page => preloadImage(page?.imageUrl));
    }
  }, [newCurrentPage, sortedPages, totalPages, isFrontCover]);

  useEffect(() => {
    setImageLoading({ left: !!leftPage, right: !!rightPage });
    setImageError({ left: false, right: false });
    setIsPageTransitioning(true);
  }, [newCurrentPage, leftPage, rightPage]);

  // Clear page transition loader when all images are loaded
  useEffect(() => {
    const leftLoaded = !leftPage || !imageLoading?.left;
    const rightLoaded = !rightPage || !imageLoading?.right;
    
    if (leftLoaded && rightLoaded) {
      setIsPageTransitioning(false);
    }
  }, [imageLoading, leftPage, rightPage]);

  // Memoized image load handler
  const handleImageLoad = useCallback((side) => {
    setImageLoading(prev => ({ ...prev, [side]: false }));
  }, []);

  const handlePageTurn = useCallback((direction) => {
    if (isFlipping) return;

    const isOddPageCatalog = totalPages % 2 !== 0;
    let targetPage;

    if (direction === 'next') {
      if (newCurrentPage === totalPages) return;
      if (isFrontCover) {
        targetPage = 2;
      } else if (isOddPageCatalog) {
        if (newCurrentPage === totalPages - 1) targetPage = totalPages;
        else if (newCurrentPage % 2 === 0) targetPage = Math.min(totalPages, newCurrentPage + 2);
        else targetPage = Math.min(totalPages, newCurrentPage + 1);
      } else {
        if (newCurrentPage === totalPages - 1 || newCurrentPage === totalPages - 2) targetPage = totalPages;
        else targetPage = Math.min(totalPages, newCurrentPage + 2);
      }
    } else {
      if (newCurrentPage === 1) return;
      if (newCurrentPage === 2) {
        targetPage = 1;
      } else if (isOddPageCatalog) {
        if (newCurrentPage === totalPages) targetPage = totalPages - 1;
        else if (newCurrentPage % 2 === 0) targetPage = Math.max(2, newCurrentPage - 2);
        else targetPage = Math.max(2, newCurrentPage - 1);
      } else {
        if (newCurrentPage === totalPages) targetPage = totalPages - 2;
        else targetPage = Math.max(2, newCurrentPage - 2);
      }
    }

    setFlipDirection(direction);
    setIsFlipping(true);

    // Para slider: pre-renderizar el spread entrante ANTES del timeout
    if (viewerMode === 'slider') {
      setIncomingSpreadData(getSpreadForPage(targetPage));
    }

    const animDuration = viewerMode === 'slider' ? 400 : 600;
    flipTimeoutRef.current = setTimeout(() => {
      onPageChange(targetPage);
      if (viewerMode === 'slider') {
        // Esperar un frame para que las imágenes nuevas (ya en caché) se pinten
        // antes de quitar la capa entrante
        requestAnimationFrame(() => {
          setIncomingSpreadData(null);
          setIsFlipping(false);
          setFlipDirection(null);
        });
      } else {
        setIsFlipping(false);
        setFlipDirection(null);
      }
    }, animDuration);
  }, [isFlipping, newCurrentPage, totalPages, isFrontCover, onPageChange, viewerMode, getSpreadForPage]);

  const handleZoom = useCallback((action) => {
    if (action === 'in' && zoomLevel < 2) {
      setZoomLevel(prev => Math.min(prev + 0.25, 2));
    } else if (action === 'out' && zoomLevel > 0.5) {
      setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    } else if (action === 'reset') {
      setZoomLevel(1);
    }
  }, [zoomLevel]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      viewerRef?.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleHotspotClick = useCallback((hotspot) => {
    const catalogId = catalog?.id;
    const pageNumber = hotspot?.pageNumber;
    const sku = hotspot?.sku;

    // Handle action immediately for instant UI response
    if (hotspot?.type === 'link') {
      window.open(hotspot?.url, '_blank', 'noopener,noreferrer');
    } else if (hotspot?.type === 'buy') {
      if (!sku) {
        return;
      }
      // Open modal immediately - don't wait for analytics
      setSelectedProductSku(sku);
      setSelectedProductStoreUrl(hotspot?.vtexStoreUrl || null);
      setProductModalOpen(true);
    } else if (hotspot?.type === 'internal_link') {
      const targetPage = hotspot?.internalLinkPage;
      if (targetPage && onPageChange) {
        onPageChange(targetPage);
      }
    }

    // Defer analytics tracking to not block UI
    if (catalogId && companyId) {
      // Use requestIdleCallback or setTimeout to defer tracking
      const deferTracking = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
      deferTracking(() => {
        analyticsService?.trackHotspotClick(
          catalogId,
          pageNumber,
          hotspot?.type,
          hotspot?.id,
          companyId
        );

        if (hotspot?.type === 'buy' && sku) {
          analyticsService?.trackBuyClick(
            catalogId,
            pageNumber,
            hotspot?.id,
            sku,
            hotspot?.quantity || 1,
            companyId
          );
        }
      });
    }
  }, [catalog?.id, companyId, onPageChange]);

  // Memoized hotspot handlers
  const handleHotspotHover = useCallback((hotspotId) => {
    setHoveredHotspot(hotspotId);
  }, []);

  const handleHotspotLeave = useCallback(() => {
    setHoveredHotspot(null);
  }, []);

  const renderPage = useCallback((pageData, side, pageIndex) => {
    if (!pageData) {
      return null;
    }

    const hotspots = (catalog?.hotspots || [])?.filter(h => h?.pageNumber === pageData?.pageNumber);const isLoading = imageLoading?.[side];
    const hasError = imageError?.[side];

    return (
      <div ref={(el) => { pageContainerRefs.current[side] = el; }} className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50 rounded-lg">
            <LoadingSpinner size="md" />
          </div>
        )}
        {hasError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
            <div className="text-center p-4">
              <Icon name="ImageOff" size={48} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground mb-1">Failed to load page image</p>
              <p className="text-xs text-muted-foreground">Page {pageData?.pageNumber}</p>
              {!pageData?.imageUrl && (
                <p className="text-xs text-destructive mt-2">No image URL available</p>
              )}
              {embedMode && (
                <p className="text-xs text-muted-foreground mt-2">Check browser console for details</p>
              )}
            </div>
          </div>
        ) : (
          <Image
            src={pageData?.imageUrl || ''}
            alt={pageData?.imageAlt || `Page ${pageData?.pageNumber}`}
            className="w-full h-full object-contain rounded-lg relative z-0"
            loading="eager"
            onLoad={() => {
              handleImageLoad(side);
            }}
            onError={() => {
              console.error(`[FLIPBOOK] Failed to load image - Page ${pageData?.pageNumber}`);
              handleImageLoad(side);
              setImageError(prev => ({ ...prev, [side]: true }));
            }}
          />
        )}
        
        {/* Hotspots */}
        {!hasError && hotspots?.map((hotspot) => (
          <HotspotMarker
            key={hotspot?.id}
            hotspot={hotspot}
            side={side}
            pageContainerRefs={pageContainerRefs}
            hoveredHotspot={hoveredHotspot}
            onHover={() => handleHotspotHover(hotspot?.id)}
            onLeave={handleHotspotLeave}
            onClick={() => handleHotspotClick(hotspot)}
          />
        ))}
      </div>
    );
  }, [catalog?.hotspots, imageLoading, imageError, embedMode, hoveredHotspot, handleHotspotHover, handleHotspotLeave, handleHotspotClick]);

  return (
    <div ref={viewerRef} className="relative w-full h-full bg-muted/30 flex items-center justify-center overflow-hidden">
      {/* Page Transition Loading Overlay */}
      {isPageTransitioning && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-[100] pointer-events-none hidden">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-sm font-medium text-foreground">Loading page...</p>
          </div>
        </div>
      )}
      {/* Main Flipbook Container */}
      <div
        className="relative transition-transform duration-300 ease-smooth"
        style={{ transform: `scale(${zoomLevel})`, willChange: 'transform' }}
      >
        <div
          className={viewerMode === 'slider' ? 'relative overflow-hidden' : 'relative perspective-1000'}
          style={{ width: getWidthHeightStyle?.width, height: getWidthHeightStyle?.height }}
        >
          {/* Spread Container (página actual) */}
          <div
            className={`flex gap-1 h-full ${
              viewerMode === 'slider' && incomingSpreadData ? 'absolute inset-0' : ''
            } ${
              viewerMode === 'slider'
                ? isFlipping && flipDirection === 'next' ? 'animate-slide-next' : isFlipping && flipDirection === 'prev' ? 'animate-slide-prev' : ''
                : `transition-transform duration-600 ${isFlipping && flipDirection === 'next' ? 'animate-flip-next' : ''} ${isFlipping && flipDirection === 'prev' ? 'animate-flip-prev' : ''}`
            }`}
            style={{ willChange: isFlipping ? 'transform' : 'auto' }}
          >
            {/* Left Page (for spreads and back cover only) */}
            <div className={`${!leftPage ? 'w-0' : 'flex-1'} bg-card rounded-lg shadow-elevation-xl overflow-visible`}>
              {leftPage && renderPage(leftPage, 'left', leftPage?.pageNumber - 1)}
            </div>

            {/* Right Page (for spreads and front cover only) */}
            <div className={`${!rightPage ? 'w-0' : 'flex-1'} bg-card rounded-lg shadow-elevation-xl overflow-visible`}>
              {rightPage && renderPage(rightPage, 'right', rightPage?.pageNumber - 1)}
            </div>
          </div>

          {/* Spread entrante (solo slider mode) — se desliza simultáneamente */}
          {viewerMode === 'slider' && incomingSpreadData && (
            <div
              className={`absolute inset-0 flex gap-1 h-full ${
                flipDirection === 'next' ? 'animate-slide-in-right' : 'animate-slide-in-left'
              }`}
              style={{ willChange: 'transform' }}
            >
              <div className={`${!incomingSpreadData?.left ? 'w-0' : 'flex-1'} bg-card rounded-lg shadow-elevation-xl overflow-hidden`}>
                {incomingSpreadData?.left && (
                  <img
                    src={incomingSpreadData.left?.imageUrl}
                    alt={`Page ${incomingSpreadData.left?.pageNumber}`}
                    className="w-full h-full object-contain rounded-lg"
                    loading="eager"
                  />
                )}
              </div>
              <div className={`${!incomingSpreadData?.right ? 'w-0' : 'flex-1'} bg-card rounded-lg shadow-elevation-xl overflow-hidden`}>
                {incomingSpreadData?.right && (
                  <img
                    src={incomingSpreadData.right?.imageUrl}
                    alt={`Page ${incomingSpreadData.right?.pageNumber}`}
                    className="w-full h-full object-contain rounded-lg"
                    loading="eager"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Navigation Controls */}
      {showNav && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-4 md:px-6 lg:px-8 pointer-events-none">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => handlePageTurn('prev')}
            disabled={newCurrentPage === 1 || isFlipping}
            className="pointer-events-auto w-12 h-12 rounded-full shadow-elevation-lg"
          >
            <Icon name="ChevronLeft" size={24} />
          </Button>

          <Button
            variant="secondary"
            size="icon"
            onClick={() => handlePageTurn('next')}
            disabled={newCurrentPage === totalPages || totalPages - newCurrentPage === 1  || isFlipping}
            className="pointer-events-auto w-12 h-12 rounded-full shadow-elevation-lg"
          >
            <Icon name="ChevronRight" size={24} />
          </Button>
        </div>
      )}
      {/* Zoom Controls */}
      {showZoom && (
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => handleZoom('in')}
            disabled={zoomLevel >= 2}
            className="w-10 h-10 rounded-lg shadow-elevation-md"
          >
            <Icon name="ZoomIn" size={20} />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => handleZoom('out')}
            disabled={zoomLevel <= 0.5}
            className="w-10 h-10 rounded-lg shadow-elevation-md"
          >
            <Icon name="ZoomOut" size={20} />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => handleZoom('reset')}
            disabled={zoomLevel === 1}
            className="w-10 h-10 rounded-lg shadow-elevation-md"
          >
            <Icon name="Maximize2" size={20} />
          </Button>
        </div>
      )}
      {/* Fullscreen Toggle */}
      {showFullscreen && (
        <div className="absolute top-4 left-4">
          <Button
            variant="secondary"
            size="icon"
            onClick={toggleFullscreen}
            className="w-10 h-10 rounded-lg shadow-elevation-md"
          >
            <Icon name={isFullscreen ? 'Minimize2' : 'Maximize'} size={20} />
          </Button>
        </div>
      )}
      {/* Page Counter */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
        <div className="bg-card/95 backdrop-blur-sm border border-border rounded-full px-4 py-2 shadow-elevation-md">
          <p className="text-sm font-medium text-foreground whitespace-nowrap">
            {isFrontCover ? t('catalogViewer.toolbar.frontCover') : isBackCover ? t('catalogViewer.toolbar.backCover') : `${t('catalogViewer.toolbar.pages')} ${leftPage?.pageNumber}-${rightPage?.pageNumber}`} ({newCurrentPage} / {totalPages})
          </p>
        </div>
      </div>
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }

        @keyframes flip-next {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(-180deg); }
        }

        @keyframes flip-prev {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(180deg); }
        }

        .animate-flip-next {
          animation: flip-next 0.6s ease-in-out;
          transform-origin: left center;
        }

        .animate-flip-prev {
          animation: flip-prev 0.6s ease-in-out;
          transform-origin: right center;
        }

        @keyframes slide-next {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }

        @keyframes slide-prev {
          0% { transform: translateX(0); }
          100% { transform: translateX(100%); }
        }

        @keyframes slide-in-from-right {
          0% { transform: translateX(100%); }
          100% { transform: translateX(0); }
        }

        @keyframes slide-in-from-left {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }

        .animate-slide-next {
          animation: slide-next 0.4s ease-in-out forwards;
        }

        .animate-slide-prev {
          animation: slide-prev 0.4s ease-in-out forwards;
        }

        .animate-slide-in-right {
          animation: slide-in-from-right 0.4s ease-in-out forwards;
        }

        .animate-slide-in-left {
          animation: slide-in-from-left 0.4s ease-in-out forwards;
        }
      `}</style>
      {/* Product Modal */}
      <ProductModal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        skuId={selectedProductSku}
        storeUrl={selectedProductStoreUrl}
        vtexAccountName={catalog?.company?.vtexAccountName}
        companyId={companyId}
        isEmbed={embedMode}
      />
    </div>
  );
};

export default React.memo(FlipbookViewer);